import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SchemaOrg from '@/components/feature/SchemaOrg';
import { testimonials } from '@/mocks/testimonials';
import { faqs } from '@/mocks/faqs';

const ContactForm = () => {
  const [status, setStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = formData.get('website_alt');
    if (honeypot && typeof honeypot === 'string' && honeypot.trim() !== '') {
      setStatus('success');
      form.reset();
      return;
    }

    formData.delete('website_alt');

    try {
      const response = await fetch('https://readdy.ai/api/form/d9q6kcee3oq4jqumth9g', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (response.ok && parsed?.code === 'OK') {
        setStatus('success');
        form.reset();
      } else {
        const msg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '提交失败，请稍后重试';
        setErrorMsg(msg);
        setStatus('error');
      }
    } catch {
      setErrorMsg('网络错误，请检查网络连接后重试');
      setStatus('error');
    }
  };

  return (
    <div className="bg-background-100 rounded-lg p-6 md:p-8">
      <h3 className="text-lg font-semibold text-foreground-950 mb-5">给我们留言</h3>
      <form className="space-y-4" onSubmit={handleSubmit} data-readdy-form="">
        <div>
          <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1.5">姓名</label>
          <input
            id="contact-name"
            name="name"
            type="text"
            className="w-full px-3.5 py-2.5 border border-background-200/70 rounded-md text-sm bg-background-50 text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
            placeholder="请输入您的姓名"
          />
        </div>
        <div>
          <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1.5">邮箱</label>
          <input
            id="contact-email"
            name="email"
            type="email"
            className="w-full px-3.5 py-2.5 border border-background-200/70 rounded-md text-sm bg-background-50 text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
            placeholder="请输入您的邮箱"
          />
        </div>
        <div>
          <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1.5">留言内容</label>
          <textarea
            id="contact-message"
            name="message"
            rows={4}
            maxLength={500}
            className="w-full px-3.5 py-2.5 border border-background-200/70 rounded-md text-sm bg-background-50 text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors resize-none"
            placeholder="请输入您的留言内容（限500字）"
          ></textarea>
        </div>
        <input type="text" name="website_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly className="honeypot-field" />
        <button
          type="submit"
          className="w-full px-6 py-2.5 bg-primary-500 text-background-50 rounded-md text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap"
        >
          发送留言
        </button>
        {status === 'success' && (
          <p className="text-sm text-green-600 text-center">感谢您的留言，我们会尽快回复！</p>
        )}
        {status === 'error' && (
          <p className="text-sm text-red-500 text-center">{errorMsg}</p>
        )}
      </form>
    </div>
  );
};

const AboutPage = () => {
  const webPageSchema = {
    '@type': 'AboutPage',
    name: '关于我们 - 草榴社区技术讨论区',
    description: '草榴社区技术讨论区是面向技术爱好者的专业交流平台，致力于打造最纯粹的技术讨论社区。',
    url: 'https://techcommunity.cn/about',
  };

  const faqSchema = {
    '@type': 'FAQPage',
    mainEntity: faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  };

  return (
    <>
      <SchemaOrg type="Organization" />
      <SchemaOrg type="WebSite" />
      <SchemaOrg type="WebPage" data={webPageSchema} />
      <SchemaOrg type="FAQPage" data={faqSchema} />
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-16">
          <div className="bg-background-100 border-b border-background-200/70">
            <div className="max-w-7xl mx-auto px-4 md:px-6 py-8 md:py-10">
              <Breadcrumb
                items={[
                  { label: '首页', href: '/' },
                  { label: '关于我们' },
                ]}
              />
              <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mt-2">
                关于草榴社区技术讨论区
              </h1>
            </div>
          </div>

          <section className="py-14 md:py-20">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 md:gap-14 items-center">
                <div>
                  <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-5">
                    品牌介绍
                  </h2>
                  <div className="space-y-4 text-sm md:text-base text-foreground-700 leading-relaxed">
                    <p>
                      <strong>草榴社区技术讨论区</strong>成立于 2020 年，是一个面向技术爱好者的专业交流平台。我们致力于为编程开发者、安全研究人员、AI 工程师等技术从业者提供一个高质量、纯粹的技术讨论空间。
                    </p>
                    <p>
                      在这里，你可以阅读来自一线技术专家的深度技术文章，参与热门技术话题的讨论，提出你遇到的技术问题并获得专业解答。我们相信，知识的力量在于分享，技术的进步源于交流。
                    </p>
                    <p>
                      经过多年的发展，草榴社区已汇聚了超过 10,000 名活跃技术成员，涵盖编程开发、网络安全、人工智能、运维架构、数据库技术和移动开发等六大核心技术领域。社区累计发布高质量技术文章超过 500 篇，为无数技术人员提供了宝贵的学习资源和成长助力。
                    </p>
                  </div>
                </div>
                <div className="relative">
                  <img
                    src="https://readdy.ai/api/search-image?query=Modern%20collaborative%20technology%20workspace%20with%20diverse%20team%20of%20developers%20working%20together%20around%20large%20monitors%20displaying%20code%2C%20warm%20ambient%20lighting%2C%20indoor%20plants%2C%20clean%20minimalist%20aesthetic%2C%20wooden%20tables%2C%20concrete%20walls%2C%20collaborative%20creative%20atmosphere%2C%20editorial%20photography%20style%20with%20soft%20shadows%2C%20intellectual%20and%20professional%20environment&width=1200&height=800&seq=about-tech-community&orientation=landscape"
                    alt="草榴社区技术讨论区 - 技术团队协作工作场景"
                    className="w-full rounded-lg"
                    title="草榴社区技术讨论区 - 专业技术交流平台"
                  />
                </div>
              </div>
            </div>
          </section>

          <section className="py-14 md:py-20 bg-background-100">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <div className="text-center mb-10 md:mb-14">
                <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-3">
                  社区核心数据
                </h2>
                <p className="text-sm md:text-base text-foreground-600 max-w-xl mx-auto">
                  用数据说话，见证社区的持续成长与影响力
                </p>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
                <div className="bg-background-50 border border-background-200/70 rounded-lg p-6 text-center">
                  <div className="w-12 h-12 rounded-xl bg-accent-100 flex items-center justify-center mx-auto mb-3">
                    <i className="ri-article-line text-accent-600 text-xl"></i>
                  </div>
                  <div className="text-2xl md:text-3xl font-bold text-foreground-950">500+</div>
                  <div className="text-xs md:text-sm text-foreground-600 mt-1">技术文章</div>
                </div>
                <div className="bg-background-50 border border-background-200/70 rounded-lg p-6 text-center">
                  <div className="w-12 h-12 rounded-xl bg-primary-100 flex items-center justify-center mx-auto mb-3">
                    <i className="ri-user-line text-primary-600 text-xl"></i>
                  </div>
                  <div className="text-2xl md:text-3xl font-bold text-foreground-950">10K+</div>
                  <div className="text-xs md:text-sm text-foreground-600 mt-1">社区成员</div>
                </div>
                <div className="bg-background-50 border border-background-200/70 rounded-lg p-6 text-center">
                  <div className="w-12 h-12 rounded-xl bg-secondary-100 flex items-center justify-center mx-auto mb-3">
                    <i className="ri-chat-3-line text-secondary-600 text-xl"></i>
                  </div>
                  <div className="text-2xl md:text-3xl font-bold text-foreground-950">25K+</div>
                  <div className="text-xs md:text-sm text-foreground-600 mt-1">讨论回复</div>
                </div>
                <div className="bg-background-50 border border-background-200/70 rounded-lg p-6 text-center">
                  <div className="w-12 h-12 rounded-xl bg-accent-100 flex items-center justify-center mx-auto mb-3">
                    <i className="ri-folder-line text-accent-600 text-xl"></i>
                  </div>
                  <div className="text-2xl md:text-3xl font-bold text-foreground-950">6</div>
                  <div className="text-xs md:text-sm text-foreground-600 mt-1">技术领域</div>
                </div>
              </div>
            </div>
          </section>

          <section className="py-14 md:py-20">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <div className="text-center mb-10 md:mb-14">
                <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-3">
                  社区口碑
                </h2>
                <p className="text-sm md:text-base text-foreground-600 max-w-xl mx-auto">
                  来自社区成员的真实评价，是我们前进的最大动力
                </p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5">
                {testimonials.map((item) => (
                  <div
                    key={item.id}
                    className="bg-background-100 border border-background-200/70 rounded-lg p-5 md:p-6"
                  >
                    <div className="flex items-center gap-3 mb-4">
                      <img
                        src={item.avatar}
                        alt={item.name}
                        className="w-11 h-11 rounded-full object-cover"
                        title={item.name}
                      />
                      <div>
                        <div className="font-medium text-foreground-950 text-sm">{item.name}</div>
                        <div className="text-xs text-foreground-500">
                          {item.role} · {item.company}
                        </div>
                      </div>
                    </div>
                    <p className="text-sm text-foreground-700 leading-relaxed">{item.content}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <section className="py-14 md:py-20 bg-background-100">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <div className="text-center mb-10 md:mb-14">
                <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-3">
                  常见问题
                </h2>
                <p className="text-sm md:text-base text-foreground-600 max-w-xl mx-auto">
                  关于社区的常见疑问，这里都有答案
                </p>
              </div>
              <div className="max-w-3xl mx-auto space-y-3">
                {faqs.map((faq, index) => (
                  <details key={index} className="group bg-background-50 border border-background-200/70 rounded-lg">
                    <summary className="flex items-center justify-between px-5 py-4 cursor-pointer list-none">
                      <span className="text-sm font-medium text-foreground-950 pr-4">{faq.question}</span>
                      <i className="ri-arrow-down-s-line text-foreground-400 group-open:rotate-180 transition-transform text-lg flex-shrink-0"></i>
                    </summary>
                    <div className="px-5 pb-4">
                      <p className="text-sm text-foreground-600 leading-relaxed">{faq.answer}</p>
                    </div>
                  </details>
                ))}
              </div>
            </div>
          </section>

          <section className="py-14 md:py-20">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 md:gap-14">
                <div>
                  <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-5">
                    联系我们
                  </h2>
                  <div className="space-y-5">
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center flex-shrink-0">
                        <i className="ri-mail-line text-primary-600 text-lg"></i>
                      </div>
                      <div>
                        <div className="text-sm font-medium text-foreground-950 mb-1">电子邮件</div>
                        <div className="text-sm text-foreground-600">contact@techcommunity.cn</div>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center flex-shrink-0">
                        <i className="ri-map-pin-line text-primary-600 text-lg"></i>
                      </div>
                      <div>
                        <div className="text-sm font-medium text-foreground-950 mb-1">公司地址</div>
                        <div className="text-sm text-foreground-600">北京市海淀区中关村科技园创新大厦 12 层</div>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center flex-shrink-0">
                        <i className="ri-time-line text-primary-600 text-lg"></i>
                      </div>
                      <div>
                        <div className="text-sm font-medium text-foreground-950 mb-1">工作时间</div>
                        <div className="text-sm text-foreground-600">周一至周五 9:00 - 18:00（法定节假日除外）</div>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center flex-shrink-0">
                        <i className="ri-phone-line text-primary-600 text-lg"></i>
                      </div>
                      <div>
                        <div className="text-sm font-medium text-foreground-950 mb-1">联系电话</div>
                        <div className="text-sm text-foreground-600">010-8888-6666</div>
                      </div>
                    </div>
                  </div>
                </div>
                <ContactForm />
              </div>
            </div>
          </section>
        </main>
        <Footer />
      </div>
    </>
  );
};

export default AboutPage;